import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import OrderDesign from '../OrderDesign';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { API_URL } from '../config/apiConfig';

/**
 * Legendary Dark Knowledge completion reward (order or Billy Bucks fallback).
 * Same presentation as Explore Unknown battle (`BattleUI`).
 *
 * @param {{ popupId: string, rewardType: 'order' | 'billy_bucks', orderId?: number, amount?: number, onDismiss: () => void }} props
 */
export default function ExploreUnknownDarkKnowledgeRewardModal({
  popupId,
  rewardType,
  orderId,
  amount,
  onDismiss,
}) {
  const { i18n } = useTranslation();
  const [order, setOrder] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    playSoundEffect('/sounds/legendary_reveal.mp3');
  }, [popupId]);

  useEffect(() => {
    if (rewardType !== 'order') {
      setOrder(null);
      setLoadError(null);
      return undefined;
    }
    const popupOrderId = Number(orderId);
    if (Number.isNaN(popupOrderId)) {
      setLoadError('Dark Knowledge reward popup missing valid orderId');
      return undefined;
    }
    let mounted = true;
    (async () => {
      const authToken = localStorage.getItem('token');
      const uiLang = encodeURIComponent(i18n.resolvedLanguage || i18n.language || 'en-us');
      const response = await fetch(`${API_URL}/order/${popupOrderId}/full?lang=${uiLang}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch legendary reward order ${popupOrderId} (status ${response.status})`);
      }
      const fullOrder = await response.json();
      if (!mounted) return;
      setOrder({ ...fullOrder, rarity: 'legendary' });
      setLoadError(null);
    })().catch((err) => {
      if (!mounted) return;
      setLoadError(err instanceof Error ? err.message : String(err));
    });
    return () => {
      mounted = false;
    };
  }, [rewardType, orderId, i18n.resolvedLanguage, i18n.language, popupId]);

  if (rewardType === 'order' && order == null && loadError == null) {
    return null;
  }

  return createPortal(
    <div
      className="explore-unknown-reward-popup-overlay explore-unknown-reward-popup-overlay--dark-knowledge"
      onClick={onDismiss}
    >
      <div
        className="explore-unknown-reward-popup-modal explore-unknown-reward-popup-modal--dark-knowledge"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="explore-unknown-dk-reward-title"
      >
        <h3 id="explore-unknown-dk-reward-title" className="explore-unknown-reward-popup-dk-title">
          {rewardType === 'billy_bucks' ? 'Legendary Reward Unlocked' : 'Legendary Order Unlocked'}
        </h3>
        {loadError ? (
          <p className="explore-unknown-reward-popup-dk-error" role="alert">
            {loadError}
          </p>
        ) : (
          <div className="explore-unknown-reward-popup-dk-visual">
            {rewardType === 'billy_bucks' ? (
              <div className="explore-unknown-reward-popup-dk-billy">
                <img src="/images/billy_bucks.png" alt="Billy Bucks" className="explore-unknown-reward-popup-dk-billy-img" />
                <div className="explore-unknown-reward-popup-dk-billy-amount">
                  +{Number(amount || 0).toLocaleString()} Billy Bucks
                </div>
                <div className="explore-unknown-reward-popup-dk-billy-note">
                  All legendary orders owned. Reward converted to currency.
                </div>
              </div>
            ) : (
              <OrderDesign
                order={order}
                count={0}
                minimal={false}
                showButtons={false}
              />
            )}
          </div>
        )}
        <button type="button" className="explore-unknown-reward-popup-dk-ok" onClick={onDismiss}>
          OK
        </button>
      </div>
    </div>,
    document.body
  );
}
